<script lang="ts">
  import { fly } from 'svelte/transition';
  import { setContext } from 'svelte';
  import { Overlay } from '@athena-lib/Overlay';
  import {
    getColorContextValue,
    initializeColorContext,
  } from '@athena-lib/container-context.svelte';
  import { getBackgroundClass } from '@athena-lib/Page/utils';
  import clsx from 'clsx';
  import { drawerContextKey, type DrawerContext, type DrawerProps } from './types';

  const {
    isOpen,
    onClose,
    children,
    label,
    variant,
    navigate = (url) => history.pushState({}, '', url),
    disablePadding,
  }: DrawerProps = $props();

  // we only have two uses for this component so far, so we're grouping
  // together the props into a variant so it's easier to re-use the drawer in different contexts
  const { backgroundColor, paddingClass, direction } = $derived.by(() => {
    if (variant === 'hamburger-menu') {
      return { backgroundColor: 'dark', paddingClass: 'p-6 md:p-12', direction: 'left' } as const;
    }
    return { backgroundColor: 'medium', paddingClass: 'p-5', direction: 'right' } as const;
  });

  setContext<DrawerContext>(drawerContextKey, {
    close: () => {
      if (isOpen) {
        onClose();
      }
    },
    navigate,
  });

  $effect(() => {
    function onKeydown(e: KeyboardEvent) {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    }
    document.body.addEventListener('keydown', onKeydown);
    return () => {
      document.body.removeEventListener('keydown', onKeydown);
    };
  });

  const colorContext = $derived(initializeColorContext(getColorContextValue(backgroundColor)));
  const backgroundClass = $derived(getBackgroundClass(colorContext.value, undefined, false, false));
  const scrollBarClass = $derived.by(() => {
    if (backgroundColor === 'dark') {
      return 'scrollbar-thin scrollbar-track-st-gray-600 scrollbar-thumb-st-black scrollbar-track-rounded-full scrollbar-thumb-rounded-full';
    }
  });
</script>

<Overlay {isOpen} {onClose} {label} lockBodyScrollDeviceSize="sm">
  <div
    class={clsx(
      'scrollbar-y-auto fixed bottom-0 top-0 w-[100vw] overflow-auto md:max-w-[30rem]',
      !disablePadding && paddingClass,
      direction === 'left' ? 'left-0' : 'right-0',
      backgroundClass,
      scrollBarClass,
    )}
    transition:fly={{ duration: 50, x: direction === 'left' ? -100 : 100 }}
    aria-modal="true"
  >
    {@render children()}
  </div>
</Overlay>
